import { midnightConfig, midnightMarketsByChain } from '@1delta/data-sdk'
import { Call } from '../../../utils/multicall'

/**
 * Number of multicall slots one Midnight market consumes: `position(id,user)`
 * (1) + `updatePositionView(market,id,user)` (1) + one `collateral(id,user,i)`
 * per collateral leg. The parser must walk `midnightMarketsByChain` in the SAME
 * order to slice results correctly.
 *
 * `position` carries the borrower's debt + collateral bitmap; `updatePositionView`
 * carries the LENDER's up-to-date credit/pendingFee (the raw `position` credit /
 * pendingFee are stale — the contract accrues the continuous fee + loss-factor
 * slashing only inside `updatePositionView`).
 */
export const midnightMarketCallCount = (collateralCount: number) =>
  2 + collateralCount

/**
 * Build the user-data multicall for every configured Midnight market on a chain.
 *
 * Midnight has no aggregating lens (unlike Morpho): per market we read the
 * packed `position(id,user)` tuple (credit/debt/bitmap) and each collateral leg
 * via `collateral(id,user,index)`. All calls target the core Midnight contract.
 */
export const buildMidnightUserCall = (
  chainId: string,
  _lender: string,
  account: string,
): Call[] => {
  const midnight = midnightConfig()?.[chainId]?.midnight
  const markets = midnightMarketsByChain(chainId)
  if (!midnight || markets.length === 0) return []

  const calls: Call[] = []
  for (const market of markets) {
    calls.push({
      address: midnight,
      name: 'position',
      params: [market.marketId, account],
    })
    // updatePositionView(market, id, user) → up-to-date (credit, pendingFee,
    // accruedFee). Takes the full on-chain Market tuple; field order/keys mirror
    // `struct Market` in MidnightAbi (viem matches tuple components by name).
    calls.push({
      address: midnight,
      name: 'updatePositionView',
      params: [
        {
          chainId: BigInt(chainId),
          midnight,
          loanToken: market.loanToken,
          collateralParams: market.collateralParams.map((c) => ({
            token: c.token,
            lltv: BigInt(c.lltv),
            liquidationCursor: BigInt(c.liquidationCursor),
            oracle: c.oracle,
          })),
          maturity: BigInt(market.maturity),
          rcfThreshold: BigInt(market.rcfThreshold),
          enterGate: market.enterGate,
          liquidatorGate: market.liquidatorGate,
        },
        market.marketId,
        account,
      ],
    })
    for (let index = 0; index < market.collateralParams.length; index++) {
      calls.push({
        address: midnight,
        name: 'collateral',
        params: [market.marketId, account, BigInt(index)],
      })
    }
  }
  return calls
}
